import { useState, type RefObject } from 'react';

export const SMALL_STEP = 0.01;
export const LARGE_STEP = 0.02;

interface ExploreModeA11yHandlersProps {
  /** Width in px matching the parent chart */
  width: number;
  /** Ref Object to left magnifier */
  leftLineRef: RefObject<HTMLOrSVGElement>;
  /** Ref Object to right magnifier */
  rightLineRef: RefObject<HTMLOrSVGElement>;
}

export const useExploreKeyboardMovementHandlers = ({
  width,
  leftLineRef,
  rightLineRef,
}: ExploreModeA11yHandlersProps) => {
  const initialSelectionAreaWidth = width / 3;

  const [selectionAreaWidth, setSelectionAreaWidth] = useState(
    initialSelectionAreaWidth,
  );
  const [positionX, setPositionX] = useState(selectionAreaWidth);

  const windowMovement =
    (direction: 'left' | 'right') => (largeStep: boolean) => {
      const step = largeStep ? LARGE_STEP : SMALL_STEP;
      const directionLogic =
        direction === 'left' ? leftWindowMovement : rightWindowMovement;

      return directionLogic(step);
    };

  const leftWindowMovement = (step: number) => {
    const leftPosition = positionX - width * step;
    if (leftPosition >= 0) {
      setPositionX(leftPosition);
    }
  };

  const rightWindowMovement = (step: number) => {
    const rightPosition = positionX + width * step;
    if (rightPosition + selectionAreaWidth < width) {
      setPositionX(rightPosition);
    }
  };

  const leftLineMovement =
    (direction: 'left' | 'right') => (largeStep: boolean) => {
      const step = largeStep ? LARGE_STEP : SMALL_STEP;
      const moveAmount = width * step;
      const directionLogic =
        direction === 'left' ? leftLineMoveToLeft : leftLineMoveToRight;

      return directionLogic(moveAmount);
    };

  const leftLineMoveToLeft = (moveAmount: number) => {
    const leftPosition = positionX - moveAmount;
    const leftPositionWidth = selectionAreaWidth + moveAmount;

    if (leftPosition >= 0) {
      setPositionX(leftPosition);
      setSelectionAreaWidth(leftPositionWidth);
    }
  };

  const leftLineMoveToRight = (moveAmount: number) => {
    const rightPosition = positionX + moveAmount;
    const rightPositionWidth = selectionAreaWidth - moveAmount;

    if (rightPositionWidth <= 0) {
      rightLineRef.current?.focus();

      return;
    }

    if (rightPosition >= 0 && rightPosition < width) {
      setPositionX(rightPosition);
      setSelectionAreaWidth(rightPositionWidth);
    }
  };

  const rightLineMovement =
    (direction: 'left' | 'right') => (largeStep: boolean) => {
      const step = largeStep ? LARGE_STEP : SMALL_STEP;
      const moveAmount = width * step;
      const directionLogic =
        direction === 'left' ? rightLineMoveToLeft : rightLineMoveToRight;

      return directionLogic(moveAmount);
    };

  const rightLineMoveToLeft = (moveAmount: number) => {
    const leftPositionWidth = selectionAreaWidth - moveAmount;

    if (leftPositionWidth <= 0) {
      leftLineRef.current?.focus();

      return;
    }

    setSelectionAreaWidth(leftPositionWidth);
  };

  const rightLineMoveToRight = (moveAmount: number) => {
    const rightPositionWidth = selectionAreaWidth + moveAmount;

    if (rightPositionWidth + positionX < width) {
      setSelectionAreaWidth(rightPositionWidth);
    }
  };

  return {
    positionX,
    selectionAreaWidth,
    windowMovement,
    leftLineMovement,
    rightLineMovement,
  };
};
